(function (root) {
  'use strict';
  const neutral = new Set(['Modern','Traditional','Plain','Textured','Bordered','Vintage']);
  const neutralColours = new Set(['Cream','White','Grey','Silver','Beige','Taupe','Brown','Natural','Black']);
  const distinctive = new Set(['Braided/Woven','Carved','Cut & Loop','Looped','Flat-Weave','Hide','High & Low (Sculpted)','Shaggy','Sheepskin','Tip-Sheared']);
  const abstractPatterns = new Set(['Abstract','Geometric','Triangles','Blocks','Retro']);
  const intersects = (a,b) => a.some(x=>b.has(x));
  const shaggy = p => p.construction==='Shaggy' || p.texture.includes('Shaggy');
  const distance = (a,b) => !a || !b ? null : [1,3,5].reduce((sum,i)=>sum+Math.abs(parseInt(a.slice(i,i+2),16)-parseInt(b.slice(i,i+2),16)),0);
  const labCache=new Map();
  function oklab(hex) {
    if(labCache.has(hex))return labCache.get(hex);
    const [r,g,b]=[1,3,5].map(i=>parseInt(hex.slice(i,i+2),16)/255).map(v=>v<=.04045?v/12.92:((v+.055)/1.055)**2.4);
    const l=Math.cbrt(.4122214708*r+.5363325363*g+.0514459929*b);
    const m=Math.cbrt(.2119034982*r+.6806995451*g+.1073969566*b);
    const s=Math.cbrt(.0883024619*r+.2817188376*g+.6299787005*b);
    const v=[.2104542553*l+.7936177850*m-.0040720468*s,1.9779984951*l-2.4285922050*m+.4505937099*s,.0259040371*l+.7827717662*m-.8086757660*s];
    labCache.set(hex,v);return v;
  }
  // Public-domain Oklab formula: https://bottosson.github.io/posts/oklab/
  // The chroma weight is the approved local experiment, not a Delta E threshold.
  function chromaDifference(a,b) {if(!a||!b)return 0;const x=oklab(a),y=oklab(b);return 600*Math.abs(Math.hypot(x[1],x[2])-Math.hypot(y[1],y[2]));}
  function profileFor(s) {
    if(s.manual || !s.shape || !s.colours.length)return null;
    if(s.washable)return 'washable';
    if(shaggy(s))return 'shaggy';
    if(s.patterns.includes('Stripes'))return 'stripes';
    if(s.patterns.includes('Abstract'))return 'abstract';
    if(s.patterns.includes('Vintage'))return 'vintage';
    return null;
  }
  function compare(a,b) {
    for(let i=0;i<a.sort.length;i++) {if(a.sort[i]<b.sort[i])return -1;if(a.sort[i]>b.sort[i])return 1;}return 0;
  }
  function priceReference(p) {
    if(Number.isFinite(p.pricePerM2)&&p.pricePerM2>0)return p.pricePerM2;
    const rates=(p.priceRates||[]).filter(v=>Number.isFinite(v)&&v>0).sort((a,b)=>a-b);
    const mid=Math.floor(rates.length/2);
    return rates.length?(rates.length%2?rates[mid]:(rates[mid-1]+rates[mid])/2):null;
  }
  function pricePenalty(s,c) {
    const a=priceReference(s),b=priceReference(c);if(!a||!b)return 0;
    const ratio=b/a;
    return Math.min(40,Math.max(0,ratio<.75?18*Math.log2(.75/ratio):ratio>1.5?14*Math.log2(ratio/1.5):0));
  }
  function simpleMultiAbstract(s) {
    return profileFor(s)==='abstract' && s.colours.includes('Multi-Coloured') && s.patterns.every(x=>x==='Abstract'||neutral.has(x));
  }
  function rank(s,c,fallback=false) {
    const sp=new Set(s.patterns),cp=new Set(c.patterns),ss=shaggy(s),cs=shaggy(c);
    if(c.id===s.id || c.status!=='ACTIVE' || !c.url || !c.available || c.shape!==s.shape)return null;
    const d0=distance(s.primary,c.primary);
    const sameRange=!!s.range && s.range===c.range;
    const refined=simpleMultiAbstract(s);
    const rangeBonus=sameRange?(refined?20:25):0;
    const designerBonus=s.designer && s.designer===c.designer?10:0;
    const designerMemberBonus=s.designer && c.designer?3:0;
    if(ss && !s.washable && !intersects(s.patterns,new Set(['Stripes','Abstract','Vintage']))) {
      if(!cs)return null;
      if(d0!==null && d0>180)return null;
      if(d0===null && (!s.colours.length || !c.colours.length || s.colours[0]!==c.colours[0]))return null;
      const motifPenalty=sp.has('Plain') && c.patterns.some(x=>!sp.has(x)&&!neutral.has(x))?25:0;
      let texturePenalty=sp.has('Textured')&&!cp.has('Textured')?10:0;
      const st=s.texture.filter(x=>distinctive.has(x)),ct=c.texture.filter(x=>distinctive.has(x));
      if(st.length && !st.some(x=>ct.includes(x)))texturePenalty+=ct.length?20:10;
      const fabricBonus=s.fabric && s.fabric===c.fabric?(s.fabric==='Wool'?25:10):0;
      const score=(d0===null?181:d0)+motifPenalty+texturePenalty-rangeBonus-designerBonus-designerMemberBonus-fabricBonus;
      const pricingPenalty=pricePenalty(s,c);
      return {handle:c.handle,distance:d0,score:score+pricingPenalty,pricePenalty:pricingPenalty,fabricBonus,rangeBonus,designerBonus,designerMemberBonus,fallback:false,sort:[0,Number(d0===null),score+pricingPenalty,c.handle]};
    }
    if(!s.colours.length || !c.colours.length)return null;
    const washableVintage=s.washable && !ss && sp.has('Vintage');
    const soft=sp.has('Stripes')&&(sp.has('Plain')||sp.has('Textured'));
    if(s.washable && !c.washable)return null;
    if(ss && !cs && !(fallback && s.washable))return null;
    if(sp.has('Vintage')&&!cp.has('Vintage')) {
      const related=['Traditional','Central Medallion'].some(x=>sp.has(x)&&cp.has(x));
      if(!washableVintage||!related)return null;
    }
    if(sp.has('Abstract')&&!intersects(c.patterns,abstractPatterns))return null;
    if(sp.has('Stripes')&&!soft&&!cp.has('Stripes') && !(sameRange&&(cp.has('Plain')||cp.has('Textured'))) && !(fallback&&ss&&(cp.has('Plain')||cp.has('Textured'))))return null;
    if(soft&&!c.patterns.some(x=>['Stripes','Plain','Textured'].includes(x)))return null;
    if(sp.has('Bordered')&&!cp.has('Bordered'))return null;
    const required=sp.has('Abstract')||sp.has('Vintage')||(sp.has('Stripes')&&!soft);
    if(!required&&!soft&&sp.has('Plain')&&!sp.has('Bordered')&&!sp.has('Textured')&&!cp.has('Plain') && !(fallback&&s.washable&&(cp.has('Textured')||cp.has('Bordered'))))return null;
    const multi=s.colours.includes('Multi-Coloured');
    const shared=[...new Set(s.colours.filter(x=>x!=='Multi-Coloured'&&c.colours.includes(x)))];
    if(multi) {
      const named=s.colours.filter(x=>x!=='Multi-Coloured');const real=new Set(c.colours.filter(x=>x!=='Multi-Coloured'));
      if(!named.length&&!c.colours.includes('Multi-Coloured'))return null;
      if(named.length&&!c.colours.includes('Multi-Coloured')&&real.size<2&&!sameRange)return null;
    } else {
      const sameColour=s.colours[0]===c.colours[0];
      const neutralFit=neutralColours.has(s.colours[0])&&neutralColours.has(c.colours[0]);
      const nearFit=neutralFit&&d0!==null&&d0<=60;
      if(!washableVintage&&!sameColour&&!nearFit&&!(sameRange&&neutralFit))return null;
      if(d0!==null&&d0>180)return null;
    }
    let d=d0;
    if(s.patterns.some(x=>['Stripes','Abstract','Vintage'].includes(x))&&s.secondary&&c.secondary&&s.primary&&c.primary) {
      const second=distance(s.secondary,c.secondary);
      if(multi)d=Math.min(d0+second,distance(s.primary,c.secondary)+distance(s.secondary,c.primary))/2;
      else {const weight=(sp.has('Stripes')||washableVintage)? .85 : .75;d=d0*weight+second*(1-weight);}
    }
    if(refined && s.primary && s.secondary) {
      const palette=[c.primary,c.secondary,c.tertiary].filter(Boolean);
      if(palette.length>=2) {
        let best=Infinity;
        for(let i=0;i<palette.length;i++)for(let j=0;j<palette.length;j++)if(i!==j)best=Math.min(best,(distance(s.primary,palette[i])+distance(s.secondary,palette[j]))/2);
        d=best;
      }
    }
    if(!multi && d!==null && d>180)return null;
    if(multi && d!==null)d/=4;
    const fabricPenalty=s.fabric==='Wool'&&c.fabric!=='Wool'?25:0;
    const stripePenalty=sp.has('Stripes')&&!cp.has('Stripes')?15:0;
    const abstractPenalty=sp.has('Abstract')&&!cp.has('Abstract')?10:0;
    const sharedSpecific=c.patterns.filter(x=>sp.has(x)&&!neutral.has(x)&&!['Stripes','Abstract'].includes(x)).length;
    const patternBonus=15*sharedSpecific;
    const st=s.texture.filter(x=>distinctive.has(x)),ct=c.texture.filter(x=>distinctive.has(x));
    let texturePenalty=!st.length || st.some(x=>ct.includes(x))?0:ct.length?20:10;
    if(sp.has('Textured')&&!cp.has('Textured'))texturePenalty+=10;
    if(ss&&!cs)texturePenalty+=30;
    const stylePenalty=sp.has('Vintage')&&s.style&&c.style&&s.style!==c.style?5:0;
    const motifPenalty=sp.has('Stripes')&&c.patterns.some(x=>!sp.has(x)&&!neutral.has(x)&&x!=='Stripes')?20:0;
    const chromaPenalty=washableVintage?chromaDifference(s.primary,c.primary):0;
    const familyPenalty=washableVintage&&!c.colours.includes(s.colours[0])?15:0;
    const paletteRefined=refined && s.primary && s.secondary && [c.primary,c.secondary,c.tertiary].filter(Boolean).length>=2;
    const neutralLabels=new Set(['White','Cream','Grey','Silver','Beige','Taupe','Natural','Black']);
    const labelBonus=multi?(paletteRefined?shared.reduce((n,x)=>n+(neutralLabels.has(x)?2:10),0):10*shared.length):0;
    const extraPatternPenalty=paletteRefined?15*c.patterns.filter(x=>!sp.has(x)&&!neutral.has(x)).length:0;
    const pricingPenalty=pricePenalty(s,c);
    const score=(d===null?181:d)+extraPatternPenalty+pricingPenalty+chromaPenalty+familyPenalty+motifPenalty+fabricPenalty+stripePenalty+abstractPenalty+texturePenalty+stylePenalty-rangeBonus-designerBonus-designerMemberBonus-labelBonus-patternBonus;
    const motifTier=Number(soft&&c.patterns.some(x=>!sp.has(x)&&!neutral.has(x)&&x!=='Stripes'));
    return {handle:c.handle,distance:d,score,pricePenalty:pricingPenalty,extraPatternPenalty,rangeBonus,designerBonus,designerMemberBonus,chromaPenalty,fabricPenalty,fallback,sort:[Number(fallback),motifTier,sp.has('Abstract')?-sharedSpecific:0,Number(d===null),score,c.handle]};
  }
  function select(s,products,limit=4) {
    if(s.manual)return [];
    const unique=[...new Map(products.map(c=>[c.id,c])).values()];
    const candidates=unique.map(c=>rank(s,c)).filter(Boolean);
    if(candidates.length<4 && (s.washable||shaggy(s))) {
      const seen=new Set(candidates.map(x=>x.handle));
      for(const c of unique)if(!seen.has(c.handle)){const v=rank(s,c,true);if(v)candidates.push(v);}
    }
    return candidates.sort(compare).slice(0,limit);
  }
  const api={rank,select,compare,profileFor,chromaDifference,distance,priceReference,pricePenalty};
  if(typeof module!=='undefined'&&module.exports)module.exports=api;
  if(root)root.LoveRugsProfiles=api;
})(typeof window==='undefined'?null:window);

(function(root){
  'use strict';
  if(!root)return;
  const api=root.LoveRugsProfiles;
  const pools=new Map();
  function normalize(p){
    return {...p,patterns:(p.patterns||[]).filter(Boolean),colours:(p.colours||[]).filter(Boolean),texture:p.texture||[],primary:/^#[0-9a-f]{6}$/i.test(p.primary||'')?p.primary:null,secondary:/^#[0-9a-f]{6}$/i.test(p.secondary||'')?p.secondary:null,tertiary:/^#[0-9a-f]{6}$/i.test(p.tertiary||'')?p.tertiary:null,pricePerM2:api.priceReference(p)};
  }
  function urlFor(path,params){
    const url=new URL(path,root.location.origin);
    const preview=new URL(root.location.href).searchParams.get('preview_theme_id');
    if(preview)url.searchParams.set('preview_theme_id',preview);
    for(const [key,value] of Object.entries(params||{}))url.searchParams.set(key,value);
    return url;
  }
  async function getFragment(url){
    for(let attempt=0;attempt<3;attempt++){
      const controller=new AbortController();const timeout=setTimeout(()=>controller.abort(),15000);
      try{
        const response=await fetch(url,{credentials:'same-origin',signal:controller.signal,priority:'low'});
        if(response.status===429&&attempt<2){
          const retry=Number(response.headers.get('Retry-After'))||4*(attempt+1);
          clearTimeout(timeout);await new Promise(resolve=>setTimeout(resolve,Math.min(15,Math.max(2,retry))*1000));continue;
        }
        if(!response.ok)throw new Error('Recommendation response '+response.status);
        return new DOMParser().parseFromString(await response.text(),'text/html');
      }finally{clearTimeout(timeout);}
    }
    throw new Error('Recommendation requests remained rate limited');
  }
  async function poolPage(url,profile,page){
    const doc=await getFragment(url);const node=doc.querySelector('[data-lr-profile-feed]');
    if(!node)throw new Error('Recommendation feed unavailable');
    const data=JSON.parse(node.textContent);
    if(data.version!==1||data.pool!=='design-match-profile-'+profile||data.page!==page||!Array.isArray(data.products)||!Number.isInteger(data.pages)||data.pages<1||data.pages>40||!/^page(?:_[a-z0-9]+)?$/i.test(data.pageParam))throw new Error('Incomplete recommendation feed');
    return data;
  }
  function loadPool(profile){
    const prefix=root.Shopify?.routes?.root||'/';const path=prefix+'collections/design-match-profile-'+profile;
    if(pools.has(path))return pools.get(path);
    const task=(async()=>{
      const first=await poolPage(urlFor(path,{section_id:'recommendation-profile-feed'}),profile,1);
      const pages=[first];let next=2;
      // Read every page, with at most two concurrent requests. Never rank an incomplete pool.
      const workers=Array.from({length:Math.min(2,first.pages-1)},async()=>{
        while(next<=first.pages){const page=next++;const data=await poolPage(urlFor(path,{section_id:'recommendation-profile-feed',[first.pageParam]:page}),profile,page);
          if(data.pages!==first.pages||data.items!==first.items)throw new Error('Recommendation pool changed during pagination');pages.push(data);
        }
      });
      await Promise.all(workers);
      const rows=pages.flatMap(p=>p.products).map(normalize);
      if(rows.length!==first.items||new Set(rows.map(p=>p.id)).size!==first.items)throw new Error('Incomplete recommendation catalogue');
      return rows;
    })();
    pools.set(path,task);task.catch(()=>pools.delete(path));return task;
  }
  function cardStyle(card,settings){
    const media=card.querySelector('.card--product .media');
    if(media){
      for(const kind of ['square','portrait','adapt'])media.classList.remove('media--'+kind);
      media.classList.add('media--'+(settings.image_ratio||'square'));
      if(settings.image_ratio==='adapt')media.style.paddingBottom='100%';else media.style.removeProperty('padding-bottom');
      if(!settings.show_secondary_image)media.querySelectorAll('img').forEach((img,i)=>{if(i>0)img.remove();});
    }
    const productCard=card.querySelector('.card--product');
    if(productCard)productCard.classList.toggle('card--outline',!!settings.show_image_outline);
    if(!settings.add_image_padding)card.querySelectorAll('.card__media-full-spacer').forEach(el=>el.classList.remove('card__media-full-spacer'));
    if(!settings.show_vendor)card.querySelectorAll('.card-information__wrapper .caption-with-letter-spacing').forEach(el=>{
      if(el.previousElementSibling?.classList.contains('visually-hidden'))el.previousElementSibling.remove();el.remove();
    });
    if(!settings.show_rating)card.querySelectorAll('.rating,.rating-text,.rating-count').forEach(el=>el.remove());
  }
  async function loadCard(candidate,settings){
    const prefix=root.Shopify?.routes?.root||'/';
    const doc=await getFragment(urlFor(prefix+'products/'+candidate.handle,{section_id:'recommendation-profile-card'}));
    const card=doc.querySelector('[data-lr-profile-card]');
    if(!card||card.dataset.lrProfileCard!==candidate.handle)return null;
    cardStyle(card,settings);card.dataset.profileScore=candidate.score.toFixed(4);card.dataset.profileFallback=String(!!candidate.fallback);
    return card;
  }
  async function upgrade(element){
    const sourceNode=element.querySelector('[data-lr-profile-source]');
    if(!sourceNode||element.dataset.profileState==='loading'||element.dataset.profileState==='complete')return;
    element.dataset.profileState='loading';
    try{
      const data=JSON.parse(sourceNode.textContent);const source=normalize(data.product);const profile=api.profileFor(source);
      if(!profile){element.dataset.profileState='original';return;}
      const rows=await loadPool(profile);const ranked=api.select(source,rows,12);const cards=[];
      for(let offset=0;offset<ranked.length&&cards.length<4;offset+=4){
        const batch=await Promise.all(ranked.slice(offset,offset+4).map(p=>loadCard(p,data.cards)));
        cards.push(...batch.filter(Boolean));
      }
      if(!cards.length){element.dataset.profileState='original';return;}
      const heading=document.createElement('h2');heading.className='product-recommendations__heading';heading.textContent=data.heading;
      const grid=document.createElement('ul');grid.setAttribute('role','list');grid.dataset.profileMatches=profile;
      grid.className='grid grid--2-col product-grid '+(cards.length>=4?'grid--4-col-desktop grid--quarter-max':'grid--'+cards.length+'-col-tablet');
      for(const card of cards.slice(0,4))grid.append(card);
      // Commit the complete result once; retain the existing block on any failure.
      element.replaceChildren(sourceNode,heading,grid);element.dataset.profileMode=profile;element.dataset.profileState='complete';
    }catch(error){element.dataset.profileState='original';console.warn('Related rugs kept the existing fallback.',error);}
  }
  function prefetch(){
    for(const node of document.querySelectorAll('[data-lr-profile-source]')){
      try{const source=normalize(JSON.parse(node.textContent).product);const profile=api.profileFor(source);if(profile)loadPool(profile).catch(()=>{});}catch(_error){/* Invalid metadata retains the existing route. */}
    }
  }
  api.upgrade=upgrade;api.loadPool=loadPool;
  root.dispatchEvent(new Event('lr:profile-ready'));
  // Prefetch below-the-fold data after parsing, before the shopper reaches the block.
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',prefetch,{once:true});else prefetch();
})(typeof window==='undefined'?null:window);
